import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'

import { type TimelineMessage, messageKeys, updateMessage } from '@/features/messages/api'
import { graphql } from '@/gql'
import type { MessageStreamSubscription } from '@/gql/graphql'
import { gqlClient } from '@/lib/graphql'

/**
 * Emoji on messages.
 *
 * The whole feature turns on one fact: **`Reaction.mine` is per-viewer.** The
 * server therefore never sends an aggregate over `messageStream` — that channel
 * is read by everybody in the room — it sends a delta naming who did what, and
 * this module folds it in, answering `mine` about *this* browser. The live path
 * and the refetched path agree by construction rather than by two filters that
 * can drift apart.
 */

const ReactMutation = graphql(`
  mutation React($messageId: UUID!, $emoji: String!) {
    react(messageId: $messageId, emoji: $emoji)
  }
`)

const UnreactMutation = graphql(`
  mutation Unreact($messageId: UUID!, $emoji: String!) {
    unreact(messageId: $messageId, emoji: $emoji)
  }
`)

type Delta = NonNullable<MessageStreamSubscription['messageStream']['reaction']>

/**
 * Apply one add-or-remove to whatever the cache is holding.
 *
 * Pure over the reaction list, so the optimistic path and the socket path are
 * the same function — the two ways a count can change must not be two ways of
 * computing it.
 */
export function foldReaction(
  reactions: TimelineMessage['reactions'],
  emoji: string,
  added: boolean,
  isMine: boolean,
): TimelineMessage['reactions'] {
  const existing = reactions.find((reaction) => reaction.emoji === emoji)

  if (added) {
    if (!existing) return [...reactions, { emoji, count: 1, mine: isMine }]
    // Idempotent, like the server's primary key: reacting twice is one row, so
    // a doubled event or a retried click cannot double a count.
    if (isMine && existing.mine) return reactions
    return reactions.map((reaction) =>
      reaction.emoji === emoji
        ? { ...reaction, count: reaction.count + 1, mine: reaction.mine || isMine }
        : reaction,
    )
  }

  if (!existing) return reactions
  // The other half of the idempotence above, and it is not symmetric by
  // accident: **your own removal arrives twice** — once optimistically, once as
  // the server's echo — and `mine` already being false is the proof the first
  // one landed. Without this the two decrements stack, and taking your reaction
  // back off a message two people reacted to erases theirs as well.
  if (isMine && !existing.mine) return reactions
  if (existing.count <= 1) return reactions.filter((reaction) => reaction.emoji !== emoji)
  return reactions.map((reaction) =>
    reaction.emoji === emoji
      ? { ...reaction, count: reaction.count - 1, mine: isMine ? false : reaction.mine }
      : reaction,
  )
}

/** The `REACTED` branch of `messageStream`. */
export function applyReaction(
  client: QueryClient,
  nookId: string,
  delta: Delta,
  myId: string | undefined,
) {
  updateMessage(client, nookId, delta.messageId, (message) => ({
    ...message,
    reactions: foldReaction(message.reactions, delta.emoji, delta.added, delta.userId === myId),
  }))
}

/**
 * React, or take it back.
 *
 * Optimistic, and it must be: an emoji that waits for a round trip before
 * appearing is an emoji people click twice. The server echo arrives on
 * `messageStream` and `foldReaction` is idempotent, so the two agreeing costs
 * nothing — and a failure rolls the one message back rather than refetching a
 * page.
 */
export function useToggleReaction(nookId: string) {
  const client = useQueryClient()

  return useMutation({
    // Two documents, one boolean result — the mutation returns whether it
    // changed anything, and which of the two was called is not news to a caller
    // that just decided it.
    mutationFn: async ({
      messageId,
      emoji,
      on,
    }: {
      messageId: string
      emoji: string
      on: boolean
    }): Promise<boolean> =>
      on
        ? (await gqlClient.request(ReactMutation, { messageId, emoji })).react
        : (await gqlClient.request(UnreactMutation, { messageId, emoji })).unreact,

    onMutate: ({ messageId, emoji, on }) => {
      const previous = client.getQueryData(messageKeys.timeline(nookId))

      updateMessage(client, nookId, messageId, (message) => ({
        ...message,
        reactions: foldReaction(message.reactions, emoji, on, true),
      }))

      return { previous }
    },

    onError: (_error, _variables, context) => {
      if (context?.previous) {
        client.setQueryData(messageKeys.timeline(nookId), context.previous)
      }
    },
  })
}
